/* eslint-disable jsdoc/require-param */
import { MedplumClient, resolveId } from '@medplum/core';
import { Attachment, Media, Specimen } from '@medplum/fhirtypes';
import { PhotoFile } from '@/pages/ainigma/patient/examination/photos/PhotoInput';
import { ARKON } from '@consts/systems.consts';

async function createAttachmentFromPhotoFile(
  medplum: MedplumClient,
  photoFile: PhotoFile
): Promise<Attachment> {
  const attachment = await medplum.createAttachment({
    data: photoFile.file,
    contentType: photoFile.file.type || 'image/jpeg',
    filename: photoFile.file.name,
  });
  return {
    ...attachment,
    size: photoFile.file.size,
  };
}

function buildSpecimenMediaResource(
  patientReference: string,
  photoFile: PhotoFile,
  attachment: Attachment,
  specimenId: string
): Media {
  return {
    resourceType: 'Media',
    status: 'completed',
    type: { text: 'Image' },
    content: attachment,
    subject: { reference: patientReference },
    partOf: [{ reference: `Specimen/${specimenId}` }],
    createdDateTime: photoFile.timestamp.toISOString(),
    extension: [
      {
        url: ARKON.SD.MEDIA_SOURCE,
        valueString: photoFile.source,
      },
    ],
  };
}

// Loads photos (Media resources) for a specimen by reading extension references.
export async function loadSpecimenPhotos(
  medplum: MedplumClient,
  specimen: Specimen
): Promise<{ photos: PhotoFile[]; existingMedia: Media[] }> {
  const mediaExtensions = (specimen.extension || []).filter(
    (ext) => ext.url === ARKON.SD.SPECIMEN_MEDIA
  );

  if (mediaExtensions.length === 0) {
    return { photos: [], existingMedia: [] };
  }

  const mediaIds = mediaExtensions
    .map((ext) => resolveId(ext.valueReference))
    .filter((id): id is string => !!id);

  const results = await Promise.allSettled(
    mediaIds.map((id) => medplum.readResource('Media', id))
  );

  const existingMedia: Media[] = results.flatMap((result) =>
    result.status === 'fulfilled' ? [result.value] : []
  );

  const photos: PhotoFile[] = existingMedia.map((media) => {
    const attachment = media.content;
    const sourceExtension = media.extension?.find(
      (ext) => ext.url === ARKON.SD.MEDIA_SOURCE
    );
    const source = (
      sourceExtension?.valueString === 'camera' ? 'camera' : 'upload'
    ) as 'camera' | 'upload';

    const fileName = attachment?.title || `photo_${media.id}.jpg`;
    const fileType = attachment?.contentType || 'image/jpeg';
    const lastModified = media.createdDateTime
      ? new Date(media.createdDateTime).getTime()
      : Date.now();

    const file = new File([], fileName, { type: fileType, lastModified });

    return {
      id: media.id || '',
      file,
      preview: attachment?.url || '',
      timestamp: media.createdDateTime
        ? new Date(media.createdDateTime)
        : new Date(),
      source,
    };
  });

  return { photos, existingMedia };
}

/**
 * Saves photos for a specimen:
 * - Creates Media resources for new photos (no id) in parallel
 * - Deletes Media resources for removed photos in parallel
 * - Updates Specimen.extension with the new set of specimen-media references
 */
export async function saveSpecimenPhotos(
  medplum: MedplumClient,
  specimen: Specimen,
  photos: PhotoFile[],
  existingMedia: Media[],
  patientReference: string
): Promise<void> {
  if (!specimen.id) {
    return;
  }

  const specimenId = specimen.id;

  // Create Media resources for new photos in parallel
  const newPhotos = photos.filter((p) => !p.id);
  const uploadResults = await Promise.allSettled(
    newPhotos.map(async (photo) => {
      const attachment = await createAttachmentFromPhotoFile(medplum, photo);
      const mediaResource = buildSpecimenMediaResource(
        patientReference,
        photo,
        attachment,
        specimenId
      );
      return medplum.createResource<Media>(mediaResource);
    })
  );

  const newMediaRefs: string[] = [];
  for (const result of uploadResults) {
    if (result.status === 'fulfilled' && result.value.id) {
      newMediaRefs.push(`Media/${result.value.id}`);
    } else if (result.status === 'rejected') {
      console.error('Error creating specimen photo:', result.reason);
    }
  }

  // Delete removed Media resources in parallel
  const removedMedia = existingMedia.filter(
    (m) => !photos.some((p) => p.id === m.id)
  );
  const deleteResults = await Promise.allSettled(
    removedMedia
      .filter((m): m is Media & { id: string } => !!m.id)
      .map((m) => medplum.deleteResource('Media', m.id))
  );
  for (const result of deleteResults) {
    if (result.status === 'rejected') {
      console.error('Failed to delete media:', result.reason);
    }
  }

  // Build the new extension array
  const otherExtensions = (specimen.extension || []).filter(
    (ext) => ext.url !== ARKON.SD.SPECIMEN_MEDIA
  );
  const keptMediaRefs = existingMedia
    .filter((m) => photos.some((p) => p.id === m.id) && m.id)
    .map((m) => `Media/${m.id}`);

  const allMediaRefs = [...keptMediaRefs, ...newMediaRefs];
  const newMediaExtensions = allMediaRefs.map((ref) => ({
    url: ARKON.SD.SPECIMEN_MEDIA,
    valueReference: { reference: ref },
  }));
  const newExtensions = [...otherExtensions, ...newMediaExtensions];

  // Patch the Specimen extension
  const hadExtensions = (specimen.extension || []).length > 0;
  if (newExtensions.length === 0) {
    if (hadExtensions) {
      await medplum.patchResource('Specimen', specimenId, [
        { op: 'remove', path: '/extension' },
      ]);
    }
  } else if (!hadExtensions) {
    await medplum.patchResource('Specimen', specimenId, [
      { op: 'add', path: '/extension', value: newExtensions },
    ]);
  } else {
    await medplum.patchResource('Specimen', specimenId, [
      { op: 'replace', path: '/extension', value: newExtensions },
    ]);
  }
}
